import {
  AlertOutlined,
  BarChartOutlined,
  CalendarOutlined,
  ClusterOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  ProfileOutlined,
  SyncOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { useCanViewSchedule } from 'horizon/routes/Schedule/useCanViewSchedule';
import {
  ANALYTICS_TAB_VISIT,
  FLEET_TAB_VISIT,
  INSPECTIONS_TAB_VISIT,
  TASKS_TAB_VISIT,
  WORK_ORDERS_TAB_VISIT,
} from 'utils/access-control/rules';
import { useAccountContext } from 'utils/account/AccountContext';
import { useFeatures } from 'utils/features';
import { ALL_WORK_ORDERS_SCHEDULE, TEST_RT } from 'utils/release-toggles';
import { notEmpty } from 'utils/types';
import { usePermissions } from 'utils/usePermissions';

import { CMS as CMSIcon, Finance, Performance, Solar } from './customIcons';
import { type Tab } from './types';

export function useNavTabs() {
  const { t } = useTranslation();
  const {
    WORK_ORDERS,
    SISENSE,
    ALERT_MANAGER,
    DEVICE_FIRMWARE_MANAGEMENT,
    HORIZON_DAMAGES,
    LEGACY_DAMAGES,
    CMS,
    FINANCE,
    PERFORMANCE,
    SOLAR,
    UPLOADS,
  } = useFeatures().features;

  const { hasReleaseToggle } = useAccountContext();

  const { canAccess } = usePermissions();
  const canViewSchedule = useCanViewSchedule();

  const inspectionsLink: string = HORIZON_DAMAGES
    ? '/damages2'
    : LEGACY_DAMAGES
      ? '/damages'
      : '/inspections';

  const tabs: Tab[] = [
    // this is a menu item to test release toggles
    hasReleaseToggle(TEST_RT)
      ? {
          title: t('navigation.releaseToggles', 'Release Toggles'),
          icon: <ExperimentOutlined />,
          link: '/',
          id: 'sidebar-rt-button',
          rules: [true],
        }
      : undefined,
    {
      title: t('navigation.fleet', 'Fleet'),
      icon: <EnvironmentOutlined />,
      link: '/fleet',
      subRoutes: ['/assets'],
      id: 'sidebar-fleet-button',
      rules: [FLEET_TAB_VISIT],
    },
    {
      title: t('navigation.inspections', 'Inspections'),
      icon: <ProfileOutlined />,
      link: inspectionsLink,
      subRoutes: [
        '/inspections',
        '/inspection-uploads',
        ...(LEGACY_DAMAGES ? ['/damages'] : []),
        ...(HORIZON_DAMAGES ? ['/damages2'] : []),
      ],
      id: 'sidebar-inspections-button',
      rules: [INSPECTIONS_TAB_VISIT],
    },
    {
      title: t('navigation.reports', 'Reports'),
      icon: <DownloadOutlined />,
      link: '/reports',
      subRoutes: ['/report-deliveries'],
      id: 'sidebar-reports-button',
      rules: [true], // shorthand for anyone with the feature flag can view
    },
    WORK_ORDERS
      ? {
          title: t('navigation.workOrders', 'Work Orders'),
          icon: <ToolOutlined />,
          link: '/work-orders',
          subRoutes: ['/tasks', '/unplanned-tasks', '/campaigns'],
          id: 'sidebar-work-orders-button',
          rules: [WORK_ORDERS_TAB_VISIT],
        }
      : {
          title: t('workOrder.task', 'Tasks'),
          icon: <ToolOutlined />,
          link: '/tasks',
          id: 'sidebar-tasks-button',
          rules: [TASKS_TAB_VISIT],
        },
    hasReleaseToggle(ALL_WORK_ORDERS_SCHEDULE) && canViewSchedule
      ? {
          title: t('navigation.schedule', 'Schedule'),
          icon: <CalendarOutlined />,
          link: '/schedule',
          id: 'sidebar-schedule-button',
          rules: [true],
        }
      : undefined,
    SISENSE
      ? {
          title: t('navigation.analytics', 'Analytics'),
          icon: <BarChartOutlined />,
          link: '/analytics',
          id: 'sidebar-analytics-button',
          rules: [ANALYTICS_TAB_VISIT],
        }
      : undefined,
    ALERT_MANAGER
      ? {
          title: t('navigation.alerts', 'Alerts'),
          icon: <AlertOutlined />,
          link: '/alerts',
          id: 'sidebar-alerts-button',
          rules: [true], // shorthand for anyone with the feature flag can view
        }
      : undefined,
    DEVICE_FIRMWARE_MANAGEMENT
      ? {
          title: t('navigation.deviceFirmware', 'Device Firmware Management'),
          icon: <ClusterOutlined />,
          link: '/device-firmware-management',
          id: 'sidebar-device-firmwares-button',
          rules: [true], // all with feature flag can access
        }
      : undefined,
    ...[
      {
        title: t('navigation.cms', 'CMS'),
        icon: <CMSIcon />,
        link: 'https://cms.skyspecs.com',
        id: 'sidebar-cms-button',
        rules: [true],
        target: '_blank',
        disabled: !CMS,
        disabledTooltip: (
          <>
            {t('navigation.learnMore', 'Learn more about')}{' '}
            <a href="https://skyspecs.com/cms-software/" target="_blank" rel="noreferrer">
              {t('navigation.cmsOffering', "SkySpecs' CMS offering")}
            </a>
          </>
        ),
      },
      {
        title: t('navigation.performance', 'Performance'),
        icon: <Performance />,
        noLinkTooltip: t(
          'navigation.performanceTooltip',
          'Please navigate to your unique reports dashboard'
        ),
        id: 'sidebar-performance-button',
        rules: [true],
        target: '_blank',
        disabled: !PERFORMANCE,
        disabledTooltip: (
          <>
            {t('navigation.learnMore', 'Learn more about')}{' '}
            <a href="https://skyspecs.com/performance-monitoring/" target="_blank" rel="noreferrer">
              {t('navigation.performanceOffering', "SkySpecs' Performance offering")}
            </a>
          </>
        ),
      },
      {
        title: t('navigation.finance', 'Finance'),
        icon: <Finance />,
        link: 'https://fam.fincovi.com/#/',
        id: 'sidebar-finance-button',
        rules: [true],
        target: '_blank',
        disabled: !FINANCE,
        disabledTooltip: (
          <>
            {t('navigation.learnMore', 'Learn more about')}{' '}
            <a
              href="https://skyspecs.com/services/financial-asset-management/"
              target="_blank"
              rel="noreferrer"
            >
              {t('navigation.financeOffering', "SkySpecs' Finance offering")}
            </a>
          </>
        ),
      },
      {
        title: t('navigation.solar', 'Solar'),
        icon: <Solar />,
        link: 'https://sam.skyspecs.com/',
        id: 'sidebar-solar-button',
        rules: [true],
        target: '_blank',
        disabled: !SOLAR,
        disabledTooltip: (
          <>
            {t('navigation.learnMore', 'Learn more about')}{' '}
            <a href="https://skyspecs.com/horizon-solar/" target="_blank" rel="noreferrer">
              {t('navigation.solarOffering', "SkySpecs' Solar offering")}
            </a>
          </>
        ),
      },
    ],
    UPLOADS
      ? {
          title: t('navigation.uploads', 'Uploads'),
          icon: <SyncOutlined />,
          link: '/uploads',
          id: 'sidebar-uploads-button',
          rules: [true],
        }
      : undefined,
  ]
    .filter(notEmpty)
    .filter(({ rules }) => canAccess(rules));

  return tabs;
}
